import { Pagination } from '@foam/buoy/src/components/Pagination/Pagination';
import { cn } from '@foam/buoy/src/lib/utils';
import { memo, useCallback, useMemo } from 'react';
import { ContentSearchSkeleton } from '~/components/ContentSearchSkeleton';
import { useFetchCreatorContent } from '~/hooks/api/useFetchCreatorContent';
import { SearchContentEmpty } from '~/screens/SearchContent/components';
import {
  ContentsApiResponseResultsType,
  ISearchContentResult,
  mapApiSearchContentResultsFromApiToVm,
} from '~/searchApi';

import { ContentCard } from '../../blocks/TalentContent/ContentCard/ContentCard';
import {
  MAX_SELECTION_LIMIT,
  useContentSelection,
} from '../../blocks/TalentContent/useContentSelection';
import { useAddContentStore } from '../useAddContentStore';

interface ContentCardWrapperProps {
  contentItem: ISearchContentResult;
  rawContent: ContentsApiResponseResultsType;
  isSelected: boolean;
  onCheckboxChange: (
    checked: boolean,
    content: ContentsApiResponseResultsType,
  ) => void;
  onDetailOpen: (content: ISearchContentResult) => void;
}

const ContentCardWrapper = memo(
  ({
    contentItem,
    rawContent,
    isSelected,
    onCheckboxChange,
    onDetailOpen,
  }: ContentCardWrapperProps) => {
    const handleCheckboxChange = useCallback(
      (checked: boolean) => {
        onCheckboxChange(checked, rawContent);
      },
      [onCheckboxChange, rawContent],
    );

    return (
      <div
        className="group/card relative grow cursor-pointer hover:z-10"
        data-selected={isSelected || undefined}
      >
        <ContentCard
          content={contentItem}
          onClick={onDetailOpen}
          isChecked={isSelected}
          onCheckboxChange={handleCheckboxChange}
          showMetrics
        />
      </div>
    );
  },
);

export const AddContentWithMetrics = () => {
  const { setIsContentDetailModalOpen } = useAddContentStore();
  const { creatorId, filters, platforms, sortBy, searchQuery, page, setPage } =
    useAddContentStore();
  const { handleSelectContent, getIsChecked, baseContents } =
    useContentSelection();

  const content = useFetchCreatorContent({
    creatorId: creatorId as string,
    platform: platforms.length > 0 ? platforms : undefined,
    filters: filters,
    orderBy: sortBy,
    searchQuery: searchQuery.length > 0 ? searchQuery : undefined,
    isEnabled: !!creatorId,
    page,
  });

  const transformedContent = useMemo(() => {
    if (!content.data) return [];
    return mapApiSearchContentResultsFromApiToVm(content.data.results);
  }, [content.data]);

  const handleContentDetailOpen = useCallback(
    (content: ISearchContentResult) => {
      setIsContentDetailModalOpen(true, content.contentId);
    },
    [setIsContentDetailModalOpen],
  );

  // Stable callback reference that doesn't change between renders
  const handleContentSelect = useCallback(
    (checked: boolean, rawContent: ContentsApiResponseResultsType) => {
      handleSelectContent(checked, rawContent);
    },
    [handleSelectContent],
  );

  if (content.isLoading) return <ContentSearchSkeleton />;
  if (content.error) return <p>Error</p>;
  if (content.data?.results.length === 0) {
    return (
      <SearchContentEmpty
        noFilter={
          filters.length === 0 &&
          platforms.length === 0 &&
          sortBy === 'published_at' &&
          searchQuery.length === 0
        }
      />
    );
  }

  const isLimitReached = baseContents.length >= MAX_SELECTION_LIMIT;

  return (
    <>
      <div
        className={cn(
          'group/grid flex w-full flex-wrap gap-4 px-2 after:grow-[999] after:content-[""]',
          {
            '[&_[data-selected]]:cursor-pointer [&>*:not([data-selected])]:cursor-not-allowed':
              isLimitReached,
          },
        )}
        data-limit-reached={isLimitReached || undefined}
        data-content-header
      >
        {transformedContent.map((contentItem, index) => {
          const isSelected = getIsChecked(contentItem.id);
          return (
            <ContentCardWrapper
              key={contentItem.id}
              contentItem={contentItem}
              rawContent={content.data!.results[index]}
              isSelected={isSelected}
              onCheckboxChange={handleContentSelect}
              onDetailOpen={handleContentDetailOpen}
            />
          );
        })}
      </div>
      <div className="px-2">
        <Pagination
          currentPage={page}
          onPageChange={(page) => setPage(page)}
          pageSize={content.data?.meta.size || 0}
          totalCount={content.data?.meta.total || 0}
        />
      </div>
    </>
  );
};
